import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useTheme,
  Avatar,
  alpha,
  Stack,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Description,
  GroupAdd,
  Logout,
  Work,
  Person,
  Layers,
  Dashboard,
  InsertChart,
  GridOn,
} from "@mui/icons-material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";

const drawerWidth = 260;

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  useEffect(() => {
    const storedUser = localStorage.getItem("fieldUser");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      navigate("/");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("fieldUser");
    localStorage.removeItem("fieldToken"); // YENİ EKLENEN: Token'ı temizle
    navigate("/");
    window.location.reload();
  };

  const menuItems = [
    {
      text: "Profil Arşivi",
      icon: <Description />,
      path: "/admin/profiles",
      roles: ["admin", "project_coordinator", "team_leader", "auditor"],
    },
    {
      text: "Sonda Arşivi",
      icon: <Layers />,
      path: "/admin/sondas",
      roles: ["admin", "project_coordinator", "team_leader", "auditor"],
    },
    {
      text: "Günlük Rapor",
      icon: <InsertChart />,
      path: "/admin/daily-report",
      roles: ["admin", "project_coordinator", "team_leader", "auditor"],
    },
    {
      text: "Toplu Düzenleme",
      icon: <GridOn />,
      path: "/admin/bulk-edit",
      roles: ["admin", "project_coordinator"],
    },
    {
      text: "Çalışmalar (Jobs)",
      icon: <Work />,
      path: "/admin/jobs",
      roles: ["admin"],
    },
    {
      text: "Personel Yönetimi",
      icon: <GroupAdd />,
      path: "/admin/users",
      roles: ["admin"],
    },
  ];

  const filteredMenu = menuItems.filter((item) =>
    item.roles.includes(user?.role),
  );

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar sx={{ bgcolor: "primary.main", color: "white", mb: 2 }}>
        <Dashboard sx={{ mr: 1 }} />
        <Typography
          variant="h6"
          fontWeight="800"
          sx={{ letterSpacing: "-0.5px" }}
        >
          HTC ADMIN
        </Typography>
      </Toolbar>

      <List sx={{ px: 1.5, flexGrow: 1 }}>
        {filteredMenu.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: "12px",
                  bgcolor: active
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                  color: active ? "primary.main" : "text.secondary",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    color: "primary.main",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: active ? "primary.main" : "text.secondary",
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: active ? 700 : 500,
                    fontSize: "0.9rem",
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ opacity: 0.5 }} />

      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="caption" color="text.disabled">
          v1.0.214122103101 | 2026
        </Typography>
      </Box>
    </Box>
  );

  const getRoleName = (role) => {
    if (role === "admin") return "Sistem Yöneticisi";
    if (role === "project_coordinator") return "Proje Koordinatörü";
    if (role === "team_leader") return "Ekip Lideri";
    if (role === "auditor") return "Kontrol Mühendisi";

    return "Personel";
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#f4f6f8", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: alpha("#fff", 0.8),
          backdropFilter: "blur(8px)",
          color: "text.primary",
          borderBottom: "1px solid #eaecf0",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Stack direction="row" alignItems="center">
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 2, display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              onClick={() => navigate("/")}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                minWidth: { xs: "auto", sm: 120 },
                "&:hover": {
                  "& .logo-text": {
                    color: "primary.dark",
                    transform: "scale(1.02)",
                  },
                },
              }}
            >
              <Typography
                className="logo-text"
                variant="h5"
                sx={{
                  fontWeight: 900,
                  color: "primary.main",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  transition: "all 0.2s ease-in-out",
                  fontFamily: "'Montserrat', sans-serif",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  "&::after": {
                    content: '""',
                    width: "4px",
                    height: "4px",
                    bgcolor: "secondary.main",
                    borderRadius: "50%",
                    ml: 0.5,
                    mt: 1,
                  },
                }}
              >
                HTC
              </Typography>
            </Box>
          </Stack>

          {user && (
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  textAlign: "right",
                  display: { xs: "none", sm: "block" },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "800", lineHeight: 1 }}
                >
                  {user.full_name}
                </Typography>
                <Typography
                  variant="caption"
                  color="primary.main"
                  fontWeight="600"
                >
                  {getRoleName(user.role)}
                </Typography>
              </Box>

              <Avatar
                sx={{
                  bgcolor: "secondary.main",
                  width: 36,
                  height: 36,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Person />
              </Avatar>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <IconButton
                color="error"
                onClick={handleLogout}
                sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}
              >
                <Logout fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: drawerWidth, borderRight: "none" },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              borderRight: "1px solid #eaecf0",
              boxShadow: "4px 0 24px rgba(0,0,0,0.02)",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: "64px",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
